"use client";
import { useThrottle } from "@/lib/hooks/useThrottle";
import {
  ActiveViewType,
  QuranMushaf,
  QuranProps,
  QuranProviderType,
  TranslationsListType,
  UseQuranOptionProps,
  UseQuranProps,
  UseQuranStateProps,
  UserType,
} from "@/lib/types";
import * as React from "react";

const isServer = typeof window === "undefined";
const defaultStateContext: UseQuranStateProps = {
  setQuranFont: (_) => {},
  setQuranFontSize: (_) => {},
  setQuranMushaf: (_) => {},
  setShowInsline: (_) => {},
  setShowWordTranslations: (_) => {},
  setShowTooltip: (_) => {},
  setWfwLanguage: (_) => {},
  setShowQuarnTranslation: (_) => {},
  setShowQuranTransliteration: (_) => {},
  setWfwFontSize: (_) => {},
  setShowWordTransliteration: (_) => {},
  setWfwRecitation: (_) => {},
  // setData: (_) => {},
  setBookmarks: (_) => {},
  setLastRead: (_) => {},
  setActiveTranslations: (_) => {},
  setCurPage: (_) => {},
  setCurAyah: (_) => {},
  setCurRuku: (_) => {},
  setSelection: (_) => {},
  setStyle: (_) => {},
  setNum: (_) => {},
};

const defaultOptionContext: UseQuranOptionProps = {
  curPage: 0,
  bookmarks: [],
  quranFont: "",
  quranFontSize: 1,
  quranMushaf: { type: "hafs2", lines: 0 },
  showInsline: false,
  showWordTranslations: true,
  showTooltip: true,
  wfwLanguage: "",
  showQuarnTranslation: false,
  showQuranTransliteration: false,
  wfwFontSize: 0,
  showWordTransliteration: true,
  wfwRecitation: false,
  data: {
    current: { Uthmani: { hafs1: {}, hafs2: {} }, IndoPak: { 15: {}, 16: {} } },
  },
  lastRead: [],
  activeTranslations: [],
  curAyah: 0,
  curRuku: 0,
  style: "translation",
  selection: "surah",
  num: 0,
};

const QuranContext = React.createContext<QuranProps | undefined>(undefined);
const QuranOptionsContext = React.createContext<
  UseQuranOptionProps | undefined
>(undefined);
const QuranStateUpateContext = React.createContext<
  UseQuranStateProps | undefined
>(undefined);

export const useQuranOptions = () =>
  React.useContext(QuranOptionsContext) || defaultOptionContext;

export const useQuranStateUpdate = () =>
  React.useContext(QuranStateUpateContext) || defaultStateContext;

export const useQuran = () =>
  React.useContext(QuranContext) || {
    activeView: {
      current: { surah: 0, verse: 0, juz: 0, page: 0, manzil: 0, ruku: 0 },
    },
    activeViewRef: {
      current: {
        surah: 0,
        verse: 0,
        juz: 0,
        page: 0,
        manzil: 0,
        ruku: 0,
        id: 0,
      },
    },

    isLoggedIn: false,
    isPageTransitioning: false,
    setActiveView: (_) => {},
    setIsLoggedIn: (_) => {},
    loggedInUser: { email: "", name: "", avatar: "" },
    setIsPageTransitioning: (_) => {},
    setLoggedInUser: (_) => {},
  };

export const QuranContextProvider: React.FC<QuranProviderType> = (
  props,
): React.ReactNode => {
  const context = React.useContext(QuranContext);

  if (context) return props.children;
  return <Quran {...props} />;
};

const Quran: React.FC<QuranProviderType> = ({
  settings,
  state,
  isLoggedIn: loggedIn,
  loggedInUser: userLogggedIn = null,
  children,
}) => {
  // const activeViewRef = useRef < Active;
  const data = React.useRef({
    Uthmani: { hafs1: {}, hafs2: {} },
    IndoPak: { 15: {}, 16: {} },
  });
  const [quranFont, setQuranFont] = React.useState<string>(settings.quranFont);

  const [quranFontSize, setQuranFontSize] = React.useState<number>(
    settings.quranFontSize,
  );
  const [quranMushaf, setQuranMushaf] = React.useState<QuranMushaf>(
    settings.quranMushaf,
  );
  const [showQuarnTranslation, setShowQuarnTranslation] =
    React.useState<boolean>(settings.showQuranTransliteration);
  const [showQuranTransliteration, setShowQuranTransliteration] =
    React.useState<boolean>(settings.showQuranTransliteration);
  const [showTooltip, setShowTooltip] = React.useState<boolean>(
    settings.showTooltip,
  );
  const [showInsline, setShowInsline] = React.useState<boolean>(
    settings.showInsline,
  );
  const [wfwLanguage, setWfwLanguage] = React.useState<string>(
    settings.wfwLanguage,
  );
  const [wfwFontSize, setWfwFontSize] = React.useState<number>(
    settings.wfwFontSize,
  );
  const [showWordTranslations, setShowWordTranslations] =
    React.useState<boolean>(settings.showWordTranslations);
  const [showWordTransliteration, setShowWordTransliteration] =
    React.useState<boolean>(settings.showWordTransliteration);
  const [wfwRecitation, setWfwRecitation] = React.useState<boolean>(
    settings.wfwRecitation,
  );

  const [bookmarks, setBookmarks] = React.useState<Array<string>>(
    state.bookmarks,
  );
  const [activeTranslations, setActiveTranslations] = React.useState<
    Array<TranslationsListType>
  >(state.activeTranslations);

  const [curPage, setCurPage] = React.useState<number>(state.curPage);
  const [curAyah, setCurAyah] = React.useState<number>(state.curAyah);
  const [curRuku, setCurRuku] = React.useState<number>(state.curRuku);
  const [lastRead, setLastRead] = React.useState<Array<number>>(state.lastRead);
  const [style, setStyle] = React.useState(state.activeView.style);
  const [selection, setSelection] = React.useState(state.activeView.selection);
  const [num, setNum] = React.useState<number>(state.activeView.number);

  const stateProviderValue: UseQuranStateProps = {
    setQuranFont,
    setQuranFontSize,
    setQuranMushaf,
    setShowInsline,
    setShowWordTranslations,
    setShowTooltip,
    setWfwLanguage,
    setShowQuarnTranslation,
    setShowQuranTransliteration,
    setWfwFontSize,
    setShowWordTransliteration,
    setWfwRecitation,
    setBookmarks,
    setCurAyah,
    setCurPage,
    setStyle,
    setSelection,
    setNum,
    setCurRuku,
    setActiveTranslations,
    setLastRead,
  };

  const optionsProviderValue: UseQuranOptionProps = {
    curPage,
    bookmarks,
    quranFont,
    quranFontSize,
    quranMushaf,
    showInsline,
    showWordTranslations,
    showTooltip,
    wfwLanguage,
    showQuarnTranslation,
    showQuranTransliteration,
    wfwFontSize,
    showWordTransliteration,
    wfwRecitation,
    data,
    lastRead,
    activeTranslations,
    curAyah,
    curRuku,
    style,
    selection,
    num,
  };

  React.useContext(QuranStateUpateContext);
  React.useContext(QuranOptionsContext);
  return (
    <QuranOptionsContext.Provider value={optionsProviderValue}>
      <QuranStateUpateContext.Provider value={stateProviderValue}>
        <QuranView
          state={{ current: state.activeView.current }}
          isLoggedIn={loggedIn}
          userLoggedIn={userLogggedIn}
        >
          {children}
        </QuranView>
      </QuranStateUpateContext.Provider>
    </QuranOptionsContext.Provider>
  );
};

const QuranView: React.FC<{
  state: Pick<ActiveViewType, "current">;
  children: React.ReactNode;
  isLoggedIn: boolean;
  userLoggedIn: Pick<UserType, "email" | "name" | "avatar"> | null;
}> = ({ state, children, isLoggedIn: loggedIn, userLoggedIn }) => {
  const [activeView, setActiveView] =
    React.useState<Pick<ActiveViewType, "current">>(state);

  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(loggedIn);
  const [loggedInUser, setLoggedInUser] = React.useState<Pick<
    UserType,
    "email" | "name" | "avatar"
  > | null>(userLoggedIn);

  const activeViewRef = React.useRef({ ...state.current, id: 0 });
  const [isPageTransitioning, setIsPageTransitioning] =
    React.useState<boolean>(false);

  const viewProviderValue: QuranProps = {
    loggedInUser,
    setLoggedInUser,
    setIsLoggedIn,
    isLoggedIn,
    activeView,
    activeViewRef,
    setActiveView: useThrottle({
      callback: setActiveView,
      leading: true,
      delay: 10,
    }),
    isPageTransitioning,
    setIsPageTransitioning,
  };

  return (
    <QuranContext.Provider value={viewProviderValue}>
      {children}
    </QuranContext.Provider>
  );
};

Quran.displayName = "Quran";
QuranContextProvider.displayName = "QuranContextProvider";
